/* Read-only adapter to the store's existing GeoStock storefront API.
 * No quantities, geolocation requests, pickup changes, or cart/ATC mutations.
 * Reuse the provider's configured stock thresholds, without exposing counts.
 * A failed settings request falls back to binary physical availability only.
 */
(() => {
  if (customElements.get('bd-store-availability')) return;
  const SHOP = 'better-deals-curacao.myshopify.com';
  const BASE = `https://api.geostock.app/shop/${SHOP}/variant_locations/`;
  const MAX_AGE = 60 * 1000;
  const REQUEST_TIMEOUT = 8000;
  const UNKNOWN = { state: 'unknown', label: 'Check with Store' };

  let settingsRequest;
  let settingsRequestedAt = 0;
  function loadSettings() {
    if (settingsRequest && Date.now() - settingsRequestedAt < 5 * MAX_AGE) return settingsRequest;
    settingsRequestedAt = Date.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    settingsRequest = fetch(`https://api.geostock.app/shop/${SHOP}/settings`, {
      signal: controller.signal, credentials: 'omit', cache: 'no-store', headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error('Store stock settings request failed');
      return response.json();
    }).then((payload) => payload?.settings || null).catch(() => null).finally(() => window.clearTimeout(timeout));
    return settingsRequest;
  }

  function stageFor(payload, locationId, settings) {
    if (payload?.settings?.tracked !== true || !Array.isArray(payload.locations)) return UNKNOWN;
    const location = payload.locations.find((item) => String(item?.id) === String(locationId));
    if (!location || location.enabled !== true || typeof location.stock !== 'number' || !Number.isFinite(location.stock)) return UNKNOWN;
    if (location.stock > 0 && settings?.useThresholdValues === true && Array.isArray(settings.displayLocStockText)) {
      // Match GeoStock's existing threshold ordering; never guess a cutoff.
      const threshold = settings.displayLocStockText.find((item) =>
        (typeof item.amount === 'number' && Number.isFinite(item.amount) && item.amount >= location.stock)
        || (item.amount === null && item.id > 0));
      const label = typeof threshold?.text === 'string' ? threshold.text.trim().toLowerCase() : '';
      if (label === 'low on stock' || label === 'low stock') return { state: 'low-stock', label: 'Low Stock' };
    }
    return location.stock > 0
      ? { state: 'in-stock', label: 'In Stock' }
      : { state: 'out-of-stock', label: 'Out of Stock' };
  }

  class StoreAvailability extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.rows = [...this.querySelectorAll('[data-location-id]')];
      this.onInputChange = (event) => {
        if (event.target.matches('input[name="id"]')) this.load(event.target.value);
      };
      this.onVisibility = () => {
        if (document.visibilityState === 'visible' && Date.now() - (this.updatedAt || 0) >= MAX_AGE) this.load(this.currentVariant, true);
      };
      this.productInfo?.addEventListener('change', this.onInputChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeOptions = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (!this.productInfo?.contains(data?.event?.target)) return;
          this.cancel();
          this.currentVariant = '';
          this.setStage({ state: 'unknown', label: 'Checking availability…' });
        });
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== String(this.productInfo?.dataset.section)) return;
          this.load(data?.variant?.id);
        });
      }
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('focus', this.onVisibility);
      this.refreshTimer = window.setInterval(this.onVisibility, MAX_AGE);
      const selectedInput = this.productInfo?.querySelector('product-form form input[name="id"]');
      this.load(selectedInput ? selectedInput.value : this.dataset.variantId);
    }

    disconnectedCallback() {
      this.cancel();
      this.productInfo?.removeEventListener('change', this.onInputChange);
      this.unsubscribeOptions?.();
      this.unsubscribeVariant?.();
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('focus', this.onVisibility);
      window.clearInterval(this.refreshTimer);
    }

    cancel() {
      this.controller?.abort();
      window.clearTimeout(this.timeout);
      this.controller = undefined;
    }

    setStage(stage, rows = this.rows) {
      rows.forEach((row) => {
        const status = row.querySelector('[data-stock-state]');
        if (status) status.dataset.stockState = stage.state;
        const label = row.querySelector('[data-stock-label]');
        if (label) label.textContent = stage.label;
      });
    }

    async load(variantId, force = false) {
      const id = String(variantId || '');
      if (!force && id === this.currentVariant && (this.controller || Date.now() - (this.updatedAt || 0) < MAX_AGE)) return;
      this.cancel();
      this.currentVariant = id;
      this.updatedAt = 0;
      this.setStage(UNKNOWN);
      if (this.dataset.shop !== SHOP || !/^\d{8,}$/.test(id)) return;
      const controller = new AbortController();
      this.controller = controller;
      this.setStage({ state: 'unknown', label: 'Checking availability…' });
      this.timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        const [response, settings] = await Promise.all([fetch(`${BASE}${id}`, {
          signal: controller.signal,
          credentials: 'omit',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }), loadSettings()]);
        if (!response.ok) throw new Error('Store availability request failed');
        const payload = await response.json();
        if (!this.isConnected || this.controller !== controller || this.currentVariant !== id) return;
        this.rows.forEach((row) => this.setStage(stageFor(payload, row.dataset.locationId, settings), [row]));
        this.updatedAt = Date.now();
      } catch (_) {
        if (this.isConnected && this.controller === controller && this.currentVariant === id) this.setStage(UNKNOWN);
      } finally {
        if (this.controller === controller) {
          window.clearTimeout(this.timeout);
          this.controller = undefined;
        }
      }
    }
  }
  customElements.define('bd-store-availability', StoreAvailability);
})();
